import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgCommonModule, TypedFormControl } from '@principle-theorem/ng-shared';
import {
  IPractice,
  LabJobIncomeTimestampField,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, WithRef } from '@principle-theorem/shared';

interface ILabJobTimestampOption {
  value: LabJobIncomeTimestampField;
  label: string;
}

@Component({
  selector: 'pr-practitioner-income-settings',
  templateUrl: './practitioner-income-settings.component.html',
  styleUrl: './practitioner-income-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgCommonModule, MatSelectModule],
})
export class PractitionerIncomeSettingsComponent {
  matSnackbar = inject(MatSnackBar);
  practice = input.required<WithRef<IPractice>>();
  hasMultiplePractices = input<boolean>();
  saving = signal(false);
  practiceName = computed(() => this.practice().name);

  labJobTimestampOptions: ILabJobTimestampOption[] = [
    { value: LabJobIncomeTimestampField.SentAt, label: 'Sent Date' },
    { value: LabJobIncomeTimestampField.ReceivedAt, label: 'Received Date' },
    { value: LabJobIncomeTimestampField.IssuedAt, label: 'Issued Date' },
  ];

  labJobTimestamp = new TypedFormControl<LabJobIncomeTimestampField>(
    LabJobIncomeTimestampField.IssuedAt,
    Validators.required
  );

  constructor() {
    toObservable(this.practice)
      .pipe(takeUntilDestroyed())
      .subscribe((practice) => {
        this.labJobTimestamp.patchValue(
          practice.settings.accounting?.practitionerIncome?.labJobTimestamp ??
            LabJobIncomeTimestampField.IssuedAt,
          { emitEvent: false }
        );
      });
  }

  async onChange(): Promise<void> {
    if (this.labJobTimestamp.invalid) {
      return;
    }

    const value = this.labJobTimestamp.value;
    this.saving.set(true);

    try {
      const practice = this.practice();
      const settings = {
        ...practice.settings,
        accounting: {
          ...practice.settings.accounting,
          practitionerIncome: {
            ...practice.settings.accounting?.practitionerIncome,
            labJobTimestamp: value,
          },
        },
      };
      await Firestore.patchDoc(practice.ref, { settings });
      this.matSnackbar.open('Successfully saved practitioner income setting.');
    } catch {
      this.matSnackbar.open('Failed to save practitioner income setting.');
      this.labJobTimestamp.patchValue(
        this.practice().settings.accounting?.practitionerIncome
          ?.labJobTimestamp ?? LabJobIncomeTimestampField.IssuedAt,
        { emitEvent: false }
      );
    } finally {
      this.saving.set(false);
    }
  }
}
